import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import api from "../lib/api";

export default function AIChat() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([
        { role: "assistant", text: "Hey, I'm MUVI, your artist-career assistant. Ask me anything about releasing music, building your brand, or growing as an artist." },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/chat", { session_id: sessionId, message: text });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        } catch {
            setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm unavailable right now. Please try again in a moment." }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="ai-chat-toggle"
                className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-ink text-white grid place-items-center shadow-xl hover:bg-brand-yellow hover:text-ink transition-colors"
                aria-label="Open AI assistant"
            >
                {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
            </button>

            {open && (
                <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[380px] h-[540px] max-h-[75vh] bg-white border border-neutral-200 shadow-2xl rounded-lg flex flex-col overflow-hidden" data-testid="ai-chat-panel">
                    <div className="bg-navy text-white px-5 py-4 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-brand-yellow grid place-items-center">
                            <Sparkles className="h-4 w-4 text-ink" />
                        </div>
                        <div className="leading-tight">
                            <p className="font-display font-bold">Ask MUVI</p>
                            <p className="text-[11px] text-white/50">Artist-career assistant</p>
                        </div>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-offwhite no-scrollbar">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    data-testid={`ai-msg-${m.role}`}
                                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                                        m.role === "user" ? "bg-ink text-white rounded-br-sm" : "bg-white border border-neutral-200 text-ink rounded-bl-sm"
                                    }`}
                                >
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-neutral-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                                    <span className="flex gap-1">
                                        <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                                        <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                                        <span className="h-2 w-2 rounded-full bg-neutral-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <form onSubmit={send} className="p-3 border-t border-neutral-200 flex gap-2 bg-white">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about your career…"
                            data-testid="ai-chat-input"
                            className="flex-1 bg-offwhite rounded-full px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-ink"
                        />
                        <button type="submit" disabled={busy} className="h-10 w-10 rounded-full bg-brand-yellow text-ink grid place-items-center disabled:opacity-50" data-testid="ai-chat-send">
                            <Send className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
